import { Controller } from "@hotwired/stimulus"

// Animated stepper for the HOW IT WORKS section.
// Manages: step card activation, rail progress, progress dots,
// particle injection, wishlist hero carousel (step 1),
// and split-pane redeem animation (step 3).
export default class extends Controller {
  static targets = [
    "stage", "rail", "railFill", "particles",
    "dot",
    "heroImg", "heroName", "heroPrice",
    "redeemRoot", "splitPane",
  ]

  static heroData = [
    { name: "Chanel Classic Flap", price: "R$12.5k" },
    { name: "Cartier Love",        price: "R$38.9k" },
    { name: "Baccarat Rouge 540",  price: "R$3.2k"  },
  ]

  connect() {
    this.active       = 0
    this.userInteracted = false
    this.inView       = false
    this.autoTimer    = null
    this.heroTimer    = null
    this.heroIndex    = 0
    this.redeemIndex  = 0
    this.redeemTimer  = null
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    this.#injectParticles()
    this.#observeSection()
    this.#setActive(0)
  }

  disconnect() {
    this.#stopAuto()
    this.#stopHero()
    this.#stopRedeem()
    this.observer?.disconnect()
  }

  // data-action="click->gimmo--home--how-split#activate"
  activate(event) {
    const card = event.currentTarget
    const step = parseInt(card.dataset.step, 10)
    this.#stopAuto()
    this.#setActive(step, true)
  }

  // data-action="keydown->gimmo--home--how-split#keyActivate"
  keyActivate(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this.activate(event)
    }
  }

  // data-action="click->gimmo--home--how-split#goToStep" (dots)
  goToStep(event) {
    const step = parseInt(event.currentTarget.dataset.step, 10)
    this.#stopAuto()
    this.#setActive(step, true)
  }

  // ── Private ──────────────────────────────────────────────

  #setActive(activeIndex, fromUser = false) {
    this.active = activeIndex

    const cards = this.element.querySelectorAll(".step-card[data-step]")
    cards.forEach((card, index) => {
      card.classList.toggle("is-active", index === activeIndex)
      card.classList.toggle("is-dim", index !== activeIndex)
      // Force animation restart
      if (index === activeIndex) {
        card.classList.remove("is-active")
        void card.offsetWidth
        card.classList.add("is-active")
      }
    })

    this.dotTargets.forEach((dot, index) => {
      dot.classList.toggle("is-active", index === activeIndex)
    })

    if (this.hasRailTarget) {
      this.railTarget.style.setProperty(
        "--rail-progress",
        String(activeIndex / Math.max(cards.length - 1, 1)),
      )
    }

    if (activeIndex === 0) {
      this.#setHero(0)
      this.#startHero()
    } else {
      this.#stopHero()
    }

    if (activeIndex === 2) {
      this.#setRedeem(0)
      this.#startRedeem()
    } else {
      this.#stopRedeem()
    }

    if (fromUser) this.userInteracted = true
  }

  // ── Auto-advance ─────────────────────────────────────────

  #tick() {
    if (this.userInteracted || !this.inView) return
    const cards = this.element.querySelectorAll(".step-card[data-step]")
    this.#setActive((this.active + 1) % cards.length)
  }

  #startAuto() {
    if (this.autoTimer || this.reduceMotion) return
    this.autoTimer = setInterval(() => this.#tick(), 3200)
  }

  #stopAuto() {
    if (this.autoTimer) {
      clearInterval(this.autoTimer)
      this.autoTimer = null
    }
  }

  // ── IntersectionObserver ─────────────────────────────────

  #observeSection() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.inView = entry.isIntersecting
        if (this.inView && !this.userInteracted) {
          this.#setActive(this.active)
          this.#startAuto()
        } else {
          this.#stopAuto()
        }
      })
    }, { threshold: 0.25 })
    this.observer.observe(this.element)
  }

  // ── Step 1: hero carousel ────────────────────────────────

  #setHero(heroIndex) {
    this.heroIndex = heroIndex
    const heroImages = this.element.querySelectorAll(".mini-hero-img")
    const thumbnails = this.element.querySelectorAll(".mini-thumb")
    heroImages.forEach((image, index) => image.classList.toggle("is-on", index === heroIndex))
    thumbnails.forEach((thumbnail, index) => thumbnail.classList.toggle("is-on", index === heroIndex))
    const hero = this.constructor.heroData[heroIndex]
    if (hero && this.hasHeroNameTarget) this.heroNameTarget.textContent = hero.name
    if (hero && this.hasHeroPriceTarget) this.heroPriceTarget.textContent = hero.price
  }

  #startHero() {
    if (this.heroTimer || this.reduceMotion) return
    const heroImages = this.element.querySelectorAll(".mini-hero-img")
    if (!heroImages.length) return
    this.heroTimer = setInterval(() => {
      this.#setHero((this.heroIndex + 1) % heroImages.length)
    }, 1800)
  }

  #stopHero() {
    if (this.heroTimer) {
      clearInterval(this.heroTimer)
      this.heroTimer = null
    }
  }

  // ── Step 3: split-pane redeem toggle ─────────────────────

  #setRedeem(redeemIndex) {
    this.redeemIndex = redeemIndex
    const panes = this.element.querySelectorAll(".split-pane[data-redeem]")
    panes.forEach((pane) => pane.classList.toggle("is-on", parseInt(pane.dataset.redeem, 10) === redeemIndex))
  }

  #startRedeem() {
    if (this.redeemTimer || this.reduceMotion) return
    const panes = this.element.querySelectorAll(".split-pane[data-redeem]")
    if (!panes.length) return
    this.redeemTimer = setInterval(() => {
      this.#setRedeem((this.redeemIndex + 1) % panes.length)
    }, 1800)
  }

  #stopRedeem() {
    if (this.redeemTimer) {
      clearInterval(this.redeemTimer)
      this.redeemTimer = null
    }
  }

  // ── Particles ────────────────────────────────────────────

  #injectParticles() {
    if (!this.hasParticlesTarget || this.reduceMotion) return
    const container = this.particlesTarget
    const particleCount = 16
    for (let index = 0; index < particleCount; index++) {
      const particle = document.createElement("div")
      particle.className = "how-particle"
      const duration = 9 + Math.random() * 8
      const delay = -Math.random() * duration
      particle.style.left             = `${Math.random() * 100}%`
      particle.style.animationDuration = `${duration}s`
      particle.style.animationDelay    = `${delay}s`
      const size = `${3 + Math.random() * 4}px`
      particle.style.width  = size
      particle.style.height = size
      container.appendChild(particle)
    }
  }
}
